import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './DesignManager.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DESIGN_SPECS = {
  landing_desktop: {
    label: 'Landingpage Desktop',
    dimensions: '3840 x 2160 px',
    maxSize: '3MB'
  },
  landing_mobile: {
    label: 'Landingpage Mobile',
    dimensions: '1080 x 3000 px',
    maxSize: '3MB'
  }
};

function DesignManager() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [uploading, setUploading] = useState({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/design`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching design config:', error);
      setMessage('Error loading design configuration');
    }
  };

  const handleFileUpload = async (designType, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage(`❌ ${designType}: File must be an image (JPG)`);
      return;
    }

    // Validate file size (3MB)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 3) {
      setMessage(`❌ ${designType}: File size (${fileSizeMB.toFixed(2)}MB) exceeds 3MB limit`);
      return;
    }

    setUploading(prev => ({ ...prev, [designType]: true }));
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/api/admin/design/upload/${designType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setMessage(`✅ ${DESIGN_SPECS[designType].label} uploaded successfully!`);
      
      // Refresh config
      await fetchConfig();
      
      // Auto-clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Upload error:', error);
      setMessage(`❌ ${designType}: ${error.response?.data?.detail || 'Upload failed'}`);
    } finally {
      setUploading(prev => ({ ...prev, [designType]: false }));
    }
  };

  const handleResetToDefault = async () => {
    if (!window.confirm('Are you sure you want to reset all designs to default? This will overwrite all custom uploads.')) {
      return;
    }

    try {
      await axios.post(`${API}/api/admin/design/reset`);
      setMessage('✅ All designs reset to default!');
      await fetchConfig();
      
      // Auto-clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Reset error:', error);
      setMessage('❌ Failed to reset designs');
    }
  };

  if (!config) {
    return <div className="design-manager-loading">Loading design configuration...</div>;
  }

  return (
    <div className="design-manager">
      <div className="design-manager-header">
        <button 
          className="back-button"
          onClick={() => navigate('/admin')}
        >
          ← Zurück zum Admin Panel
        </button>
        <h1>🎨 Design Manager</h1>
        <p>Upload custom graphics for your debate platform. All images must be JPG format.</p>
      </div>

      {message && (
        <div className={`design-message ${message.startsWith('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="design-grid">
        {Object.entries(DESIGN_SPECS).map(([key, spec]) => (
          <div key={key} className="design-upload-card">
            <div className="design-card-header">
              <h3>{spec.label}</h3>
              <div className="design-specs">
                <span className="spec-badge">{spec.dimensions}</span>
                <span className="spec-badge">{spec.maxSize}</span>
              </div>
            </div>

            <div className="design-preview">
              {config[key] && (
                <img 
                  src={`${config[key]}?t=${Date.now()}`} 
                  alt={spec.label}
                  onError={(e) => {
                    e.target.src = '/placeholder.jpg';
                  }}
                />
              )}
            </div>

            <div className="design-upload-area">
              <input
                type="file"
                id={`upload-${key}`}
                accept="image/jpeg,image/jpg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileUpload(key, file);
                  }
                }}
                disabled={uploading[key]}
                className="file-input"
              />
              <label 
                htmlFor={`upload-${key}`} 
                className={`upload-button ${uploading[key] ? 'uploading' : ''}`}
              >
                {uploading[key] ? '⏳ Uploading...' : '📁 Upload New Image'}
              </label>
              <div className="current-path">
                Current: {config[key] || 'Default'}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="design-actions">
        <button 
          className="reset-button"
          onClick={handleResetToDefault}
        >
          🔄 Reset All to Default
        </button>
      </div>
    </div>
  );
}

export default DesignManager;
